import { sanitizeHiddenWitnessIds } from './witnessVisibility'

export type LedgerColumnFilterTemplate = {
  id: string
  name: string
  hiddenWitnessIds: string[]
  scopeId: string
  scopeLabel: string
  createdAt: string
}

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0

export const parseStoredHiddenWitnessIds = (raw: string): string[] => {
  if (!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isNonEmptyString) : []
  } catch {
    return []
  }
}

export const sanitizeLedgerColumnHiddenWitnessIds = (
  orderedWitnessIds: string[],
  hiddenWitnessIds: string[]
): string[] => {
  const sanitized = sanitizeHiddenWitnessIds(orderedWitnessIds, hiddenWitnessIds)
  if (orderedWitnessIds.length <= 1) {
    return []
  }
  if (sanitized.length >= orderedWitnessIds.length) {
    return sanitized.slice(0, orderedWitnessIds.length - 1)
  }
  return sanitized
}

export const parseLedgerColumnFilterTemplates = (raw: string): LedgerColumnFilterTemplate[] => {
  if (!raw) {
    return []
  }
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.flatMap(entry => {
      if (!entry || typeof entry !== 'object') {
        return []
      }
      const candidate = entry as Partial<LedgerColumnFilterTemplate>
      if (!isNonEmptyString(candidate.id) || !isNonEmptyString(candidate.name)) {
        return []
      }
      return [{
        id: candidate.id,
        name: candidate.name.trim(),
        hiddenWitnessIds: Array.isArray(candidate.hiddenWitnessIds)
          ? candidate.hiddenWitnessIds.filter(isNonEmptyString)
          : [],
        scopeId: isNonEmptyString(candidate.scopeId) ? candidate.scopeId : '__all__',
        scopeLabel: isNonEmptyString(candidate.scopeLabel) ? candidate.scopeLabel : 'All witnesses',
        createdAt: isNonEmptyString(candidate.createdAt) ? candidate.createdAt : new Date(0).toISOString()
      }]
    })
  } catch {
    return []
  }
}

export const serializeLedgerColumnFilterTemplates = (templates: LedgerColumnFilterTemplate[]): string => JSON.stringify(templates)

export const buildLedgerColumnFilterTemplate = ({
  name,
  hiddenWitnessIds,
  orderedWitnessIds,
  scopeId,
  scopeLabel,
  existingId
}: {
  name: string
  hiddenWitnessIds: string[]
  orderedWitnessIds: string[]
  scopeId: string
  scopeLabel: string
  existingId?: string
}): LedgerColumnFilterTemplate => ({
  id: existingId || `ledger-column-template-${Date.now()}`,
  name: name.trim(),
  hiddenWitnessIds: sanitizeLedgerColumnHiddenWitnessIds(orderedWitnessIds, hiddenWitnessIds),
  scopeId: scopeId || '__all__',
  scopeLabel: scopeLabel || 'All witnesses',
  createdAt: new Date().toISOString()
})

export const applyLedgerColumnFilterTemplate = (
  template: LedgerColumnFilterTemplate,
  orderedWitnessIds: string[]
): string[] => sanitizeLedgerColumnHiddenWitnessIds(orderedWitnessIds, template.hiddenWitnessIds)
